import { clsx } from 'clsx'

import { Button } from '../Button/Button.js'
import { ButtonGroup } from '../Button/ButtonGroup.js'
import { SvgIcon } from '../Icon/SvgIcon.js'
import { Tooltip } from '../Tooltip/Tooltip.js'

import { FormControlLayout } from './FormControlLayout.js'
import { FormControlButtonGroupSelectProps } from './types.js'
import { extractLayoutProps } from './utils.js'

// Type guard to check if props are for multi-select mode
function isMultipleMode(
  props: FormControlButtonGroupSelectProps,
): props is Extract<FormControlButtonGroupSelectProps, { mode: 'multiple' }> {
  return props.mode === 'multiple'
}

export const FormControlButtonGroupSelect = (
  props: FormControlButtonGroupSelectProps,
) => {
  const { items, fullWidth, onChange, selectedVariant, lockedItems, ...rest } =
    props

  const { layoutProps } = extractLayoutProps(rest)

  const handleClick = (clickedValue: string) => {
    if (!onChange) {
      return
    }

    if (isMultipleMode(props)) {
      const { value: multiValue, onChange: multiOnChange } = props
      const currentSelected = multiValue || []
      const newSelected = currentSelected.includes(clickedValue)
        ? currentSelected.filter(v => v !== clickedValue)
        : [...currentSelected, clickedValue]
      multiOnChange(newSelected)
    } else {
      const { onChange: singleOnChange } = props
      singleOnChange(clickedValue)
    }
  }

  const isSelected = (itemValue: string): boolean => {
    if (isMultipleMode(props)) {
      const { value: multiValue } = props
      return multiValue.includes(itemValue)
    }
    const { value: singleValue } = props
    return singleValue === itemValue
  }

  return (
    <FormControlLayout {...layoutProps}>
      <ButtonGroup className={fullWidth ? 'w-full' : null}>
        {items.map(({ icon, text, value: itemValue }) => {
          const lockedItem = lockedItems?.find(item => item.value === itemValue)

          return (
            <Button
              key={itemValue}
              fullWidth={fullWidth}
              className="!px-1 !border-x-line"
              variant={
                isSelected(itemValue) ? selectedVariant : 'secondaryNeutral'
              }
              pressed={isSelected(itemValue)}
              onClick={() => {
                if (lockedItem) {
                  return
                }
                handleClick(itemValue)
              }}
            >
              <div
                className={clsx(
                  'flex items-center gap-2',
                  lockedItem && 'cursor-not-allowed',
                )}
              >
                {icon}
                <span className="truncate">{text}</span>
                {lockedItem && (
                  <Tooltip>
                    <Tooltip.Trigger asChild>
                      <SvgIcon name="lock" />
                    </Tooltip.Trigger>
                    <Tooltip.Panel>{lockedItem.helper}</Tooltip.Panel>
                  </Tooltip>
                )}
              </div>
            </Button>
          )
        })}
      </ButtonGroup>
    </FormControlLayout>
  )
}
